import { useState } from "react";
import { type RaRecord } from "react-admin";
import { Views, type View, type ToolbarProps, type EventPropGetter } from "react-big-calendar";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import { formatIsoDate } from "@/utils/date-format";
import { ResourceBigCalendarView } from "@/components/resource-big-calendar/ResourceBigCalendarView";
import { parseCalendarViewFromSearchParam } from "@/components/resource-big-calendar/resourceCalendarTimeGridDefaults";
import { RazaEventsViewToggle } from "./RazaEventsViewToggle";
import { calendarEventStyle } from "./calendar/calendar-event-style";
import { RazaEventsCalendarToolbarWithLegend } from "./calendar/RazaEventsCalendarToolbarWithLegend";
import { RazaEventsCalendarEventComponent } from "./calendar/RazaEventsCalendarEventComponent";
import { RazaEventsCalendarMonthDateHeader } from "./calendar/RazaEventsCalendarMonthDateHeader";
import { RazaEventsDetailsDialog } from "./calendar/RazaEventsDetailsDialog";
import { RazaEventsHallOverlayDialog } from "./calendar/RazaEventsHallOverlayDialog";
import { useRazaEventsCalendarEvents } from "./calendar/use-raza-events-calendar-events";
import { parseHallOverlaySearchParam } from "./calendar/map-hall-overlay-events";
import type { RazaEventsCalendarEvent, RazaEventsCalendarHallOverlay } from "./calendar/types";

export function RazaEventsCalendarView() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the single source of truth for view and date. Deriving them directly
  // here (instead of mirroring into useState + useEffect) means browser back/forward
  // instantly restores the correct calendar state without a competing effect
  // overwriting the URL again.
  const view: View = parseCalendarViewFromSearchParam(searchParams.get("view"), isMobile);
  const dateParam = searchParams.get("date");
  const date: Dayjs = dateParam && dayjs(dateParam).isValid() ? dayjs(dateParam) : dayjs();
  const hallOverlayEnabled = parseHallOverlaySearchParam(searchParams.get("halls"));

  const [selectedEvent, setSelectedEvent] = useState<RaRecord | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedHallOverlay, setSelectedHallOverlay] =
    useState<RazaEventsCalendarHallOverlay | null>(null);
  const [showHallModal, setShowHallModal] = useState(false);

  const events = useRazaEventsCalendarEvents(view, date, {
    includeHallOverlay: hallOverlayEnabled,
  });

  const handleViewChange = (newView: View) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("view", String(newView));
      return next;
    });
  };

  const handleDateChange = (newDate: Dayjs) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("date", formatIsoDate(newDate));
      return next;
    });
  };

  const handleHallOverlayChange = (enabled: boolean) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (enabled) {
        next.set("halls", "1");
      } else {
        next.delete("halls");
      }
      return next;
    });
  };

  const handleSelectEvent = (event: RazaEventsCalendarEvent) => {
    if (event.overlayKind === "hall" && event.hallOverlay) {
      setSelectedHallOverlay(event.hallOverlay);
      setShowHallModal(true);
      return;
    }
    if (event?.resource) {
      setSelectedEvent(event.resource);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const handleCloseHallModal = () => {
    setShowHallModal(false);
    setSelectedHallOverlay(null);
  };

  return (
    <ResourceBigCalendarView<RazaEventsCalendarEvent>
      events={events}
      view={view}
      onViewChange={handleViewChange}
      date={date}
      onDateChange={handleDateChange}
      onSelectEvent={handleSelectEvent}
      EventComponent={RazaEventsCalendarEventComponent}
      renderToolbar={(toolbarProps: ToolbarProps) => (
        <RazaEventsCalendarToolbarWithLegend
          {...toolbarProps}
          events={events}
          selectedDate={date}
          setSelectedDate={handleDateChange}
          listToggleComponent={RazaEventsViewToggle}
          hallOverlayEnabled={hallOverlayEnabled}
          onHallOverlayChange={handleHallOverlayChange}
        />
      )}
      monthDateHeader={RazaEventsCalendarMonthDateHeader}
      eventPropGetter={
        ((event: RazaEventsCalendarEvent) => ({
          style: {
            ...calendarEventStyle(muiTheme, event),
            ...(view === Views.MONTH
              ? {
                  padding: "1px 3px",
                  fontSize: "0.7rem",
                  lineHeight: 1.15,
                  minHeight: 0,
                }
              : {
                  padding: "3px 5px",
                  fontSize: "0.75rem",
                  lineHeight: 1.2,
                }),
          },
        })) as EventPropGetter<RazaEventsCalendarEvent>
      }
    >
      <RazaEventsDetailsDialog
        open={showModal}
        onClose={handleCloseModal}
        selectedEvent={selectedEvent}
      />
      <RazaEventsHallOverlayDialog
        open={showHallModal}
        onClose={handleCloseHallModal}
        overlay={selectedHallOverlay}
      />
    </ResourceBigCalendarView>
  );
}

export default RazaEventsCalendarView;
